/**
 * THE impersonation dialog. One component, two scopes.
 *
 * Foundations 2 (Users rail), B2d-T2. There were two: this one, and
 * `components/users/impersonation-dialog.tsx` — 600 lines built around a
 * two-person approval flow that auto-approved itself after a two-second
 * `setTimeout`, with an approver picker drawn from live tenant users. It is
 * deleted: the founder's rule is that there is no dual approval anywhere
 * (self-approval prevention stands), and a dialog that simulates an approval
 * nobody gave is worse than no dialog.
 *
 * ONE COMPONENT WITH A SCOPE PROP (feedback: "shared component with scope
 * prop", never two parallel files):
 *
 *   `subject` given    — the Users rail's row action. The person is already
 *                        chosen; the dialog asks only why and for how long.
 *   `subject` omitted  — the Security screen's tenant-wide control, which
 *                        picks the person first.
 *
 * WHAT IT POSTS: `POST /api/v1/users/:id/impersonate`. The admin-prefixed
 * start path it replaces was never mounted — every click 404'd.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useStartImpersonation } from '@/lib/use-users';
import { useImpersonation } from '@/contexts/impersonation-context';
import { toast } from 'sonner';

interface Person {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
}

export interface StartImpersonationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * The person to impersonate, when the caller already knows (the Users
     * rail's row action). Omit for the tenant-wide control, which shows a
     * picker.
     */
    subject?: Person | null;
}

/**
 * Sixty minutes is the ceiling (decisions.md 2026-09-08) and the server caps
 * it there whatever this list offers — so the list offers nothing longer,
 * because an option that is silently shortened is a lie told by a dropdown.
 */
const DURATIONS = ['15', '30', '60'] as const;

export function StartImpersonationDialog({
    open,
    onOpenChange,
    subject,
}: StartImpersonationDialogProps) {
    const impersonation = useImpersonation();
    const start = useStartImpersonation();
    const [users, setUsers] = useState<Person[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [reason, setReason] = useState('');
    const [duration, setDuration] = useState<string>('60');

    useEffect(() => {
        if (!open) {
            setSelectedUserId('');
            setReason('');
            setDuration('60');
            return;
        }
        if (subject) return; // the person is already chosen — no picker, no fetch
        void fetchUsers();
    }, [open, subject?.id]);

    /**
     * THE PEOPLE THE SERVER WILL LET YOU ACT AS — not the roster (review
     * B2dT2-BF-08).
     *
     * It read `/users?pageSize=100` and offered every row it got back: the
     * caller themselves, every Owner, and — in a tenant with more than a
     * hundred members — silently nobody past the hundredth. Picking an Owner
     * produced "An Owner cannot be impersonated" from a list that had just
     * offered them.
     *
     * `/users/impersonation/candidates` runs each candidate through THE rule
     * (`policy/impersonation.ts`) with this caller, so an option shown here is
     * an option the start route accepts. No filtering happens in the browser:
     * the refusals are about authority the browser cannot see.
     */
    const fetchUsers = async () => {
        try {
            setIsLoadingUsers(true);
            const response = await apiClient.get<{ data: Person[] }>(
                '/users/impersonation/candidates?pageSize=50',
            );
            setUsers(response.data);
        } catch (err) {
            console.error('Failed to fetch impersonation candidates:', err);
            toast.error(getApiErrorMessage(err, 'Could not load the user list'));
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const targetId = subject?.id ?? selectedUserId;

    const handleStartImpersonation = async () => {
        if (!targetId || reason.trim().length < 10) return;

        try {
            const response = await start.mutateAsync({
                userId: targetId,
                reason: reason.trim(),
                durationMinutes: parseInt(duration, 10),
            });

            // The context owns the token swap and the banner.
            impersonation.startImpersonation(
                response.sessionId,
                response.accessToken,
                {
                    id: response.targetUser.id,
                    email: response.targetUser.email,
                    name: response.targetUser.firstName
                        ? `${response.targetUser.firstName} ${response.targetUser.lastName || ''}`.trim()
                        : undefined,
                },
            );

            onOpenChange(false);
            // Reload so every query re-runs as the subject.
            window.location.reload();
        } catch (err) {
            // The server's refusal is the message: "An Owner cannot be
            // impersonated", "You cannot impersonate yourself", "That account
            // is not an active member of this organization".
            toast.error(getApiErrorMessage(err, 'Failed to start impersonation'));
        }
    };

    const getUserDisplay = (user: Person) => {
        if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }
        return user.email.split('@')[0];
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Start impersonation</DialogTitle>
                    <DialogDescription>
                        {subject
                            ? `You will act as ${getUserDisplay(subject)} (${subject.email}). Everything you do is recorded against your own account.`
                            : 'Select a user to act as. Everything you do is recorded against your own account.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    {!subject && (
                        <div className="space-y-2">
                            <Label htmlFor="impersonation-user">Person</Label>
                            {isLoadingUsers ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            ) : (
                                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                    <SelectTrigger id="impersonation-user">
                                        <SelectValue placeholder="Select a person to act as" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.length === 0 ? (
                                            <div className="p-2 text-sm text-muted-foreground text-center">No users found</div>
                                        ) : (
                                            users.map((user) => (
                                                <SelectItem key={user.id} value={user.id}>
                                                    {getUserDisplay(user)} ({user.email})
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="impersonation-reason">Reason (required)</Label>
                        <Textarea
                            id="impersonation-reason"
                            placeholder="Why you need to act as this person (at least 10 characters)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                            This is recorded on the session and in the audit trail.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="impersonation-duration">Duration</Label>
                        <Select value={duration} onValueChange={setDuration}>
                            <SelectTrigger id="impersonation-duration">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DURATIONS.map((minutes) => (
                                    <SelectItem key={minutes} value={minutes}>
                                        {minutes === '60' ? '1 hour (maximum)' : `${minutes} minutes`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleStartImpersonation}
                        disabled={!targetId || reason.trim().length < 10 || start.isPending}
                    >
                        {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Start impersonation
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
